import { ApiError } from '@/internationalization/api-client'
import { meetingsApi, type DeviceAudioChunk } from './api'

/**
 * "Record now": capture this device's microphone and hand it to the pipeline.
 *
 * The worklet (`public/pcm-capture-worklet.js`) produces 16 kHz mono s16le,
 * the recorder container's own capture format, so the server stores these bytes
 * as chunks and transcription cannot tell the two apart.
 *
 * CHUNKS ARE UPLOADED AS THEY ARE CAPTURED, 30 s at a time like the recorder,
 * so a closed tab or a crashed browser loses at most the chunk in progress. A
 * failed upload is retried with backoff and, if it still fails, kept in memory
 * and retried once more on stop -- a recording cannot be repeated.
 *
 * Mic permission is asked for BEFORE the meeting row exists (`open`), and frames
 * only flow once `begin` has a meeting id: a refused microphone must not leave a
 * recording behind that will never receive audio.
 *
 * A MICROPHONE THAT DISAPPEARS DOES NOT END THE RECORDING. A Bluetooth headset
 * dropping out, or the OS switching inputs, ends the track; the recorder asks
 * for the default microphone again and carries on. Only the person pressing
 * Stop ends it -- the same rule as never ending a bot recording on one signal.
 *
 * A PAGE THE BROWSER STOPS RUNNING RECORDS NOTHING, AND NO CODE IN IT CAN SAY
 * SO AT THE TIME. Measured on Android Chrome (8 Oct): with the screen off, the
 * page stopped after about a minute and a half, and 32 minutes of a 47-minute
 * meeting were never captured; the parts already captured uploaded in a burst
 * when the screen came back on. A laptop that sleeps does the same. So the
 * recorder (1) holds a screen wake lock, which stops the screen timing out --
 * the commonest cause -- though not a manual lock or an app switch; (2) counts
 * the audio it actually RECEIVED, so the time shown is the time recorded and the
 * shortfall against the wall clock is the time lost; and (3) on coming back,
 * resumes the audio, re-takes the lock and retries the parts that failed while
 * the page was away.
 */

export const TARGET_SAMPLE_RATE = 16_000
export const CHUNK_SECONDS = 30
const CHUNK_SAMPLES = TARGET_SAMPLE_RATE * CHUNK_SECONDS
const UPLOAD_ATTEMPTS = 5
const MAX_BACKOFF_MS = 15_000
const FLUSH_TIMEOUT_MS = 2_000

/**
 * Raw room audio for ASR, not a call. Noise suppression is tuned for one close
 * talker and attenuates the quieter, more distant people in a room; echo
 * cancellation removes whatever the device itself is playing -- the far end of
 * a speakerphone call somebody is recording. Gain control stays on: a laptop
 * microphone across a table is otherwise too quiet for diarization.
 */
const MIC_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: true,
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
const nowSeconds = () => Date.now() / 1000

/** Retrying cannot change these answers: the recording is gone or finished. */
function isPermanent(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 401 &&
    error.status !== 408 &&
    error.status !== 429
  )
}

function rmsOf(samples: Int16Array): number {
  if (samples.length === 0) return 0
  let sum = 0
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i] / 0x8000
    sum += v * v
  }
  return Math.sqrt(sum / samples.length)
}

export function deviceRecordingSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window.AudioWorkletNode !== 'undefined'
  )
}

export interface DeviceRecorderProgress {
  /** Chunks captured so far, including the one still being uploaded. */
  captured: number
  uploaded: number
  /** Chunks that exhausted their retries and are held for the final attempt. */
  failed: number
}

export class DeviceRecorder {
  private stream: MediaStream | null = null
  private context: AudioContext | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private node: AudioWorkletNode | null = null
  private meetingId: string | null = null

  private pending: Int16Array[] = []
  private pendingSamples = 0
  private chunkStartedAt: number | null = null
  private chunks: DeviceAudioChunk[] = []
  private uploadQueue: Promise<void> = Promise.resolve()
  private failedChunks = new Map<number, ArrayBuffer>()
  private permanentError: unknown = null
  private onFlushed: (() => void) | null = null
  private stopped = false
  private finishing: Promise<string> | null = null
  // A Set rather than a counter: a part retried by two paths (the page coming
  // back, then Stop) must not be counted as uploaded twice.
  private uploaded = new Set<number>()
  private retrying = false
  private recordedSamples = 0
  private firstAudioAt: number | null = null
  private lastAudioAt: number | null = null
  private wakeLock: WakeLockSentinel | null = null
  private readonly onVisibility = () => {
    if (document.visibilityState === 'visible') void this.resume()
  }
  private readonly onOnline = () => this.retryFailed()

  /** Live input level, 0..1, for a meter. */
  onLevel: ((rms: number) => void) | null = null
  onProgress: ((progress: DeviceRecorderProgress) => void) | null = null
  /**
   * The microphone went away and could not be re-acquired. Capture is paused,
   * not ended: what was recorded is kept and Stop still saves it.
   */
  onInputLost: (() => void) | null = null
  /** The microphone came back after `onInputLost`. */
  onInputRestored: (() => void) | null = null
  /**
   * The server refused an upload in a way retrying cannot change (the
   * recording was finished elsewhere, or the person lost the permission).
   * Capture has been stopped, because nothing more can be stored.
   */
  onRefused: ((error: unknown) => void) | null = null

  /** True once the server has refused this recording for good. */
  get refused(): boolean {
    return this.permanentError !== null
  }

  /** Audio actually received, in seconds. The honest length of the recording. */
  get recordedSeconds(): number {
    return this.recordedSamples / TARGET_SAMPLE_RATE
  }

  /**
   * Wall-clock time since the first sample minus the audio received: what was
   * NOT recorded. Measured this way rather than from gaps between frames, so
   * frames that arrive late in a burst (a busy main thread) do not count as
   * lost -- they catch up -- while time the browser was not running the page
   * stays missing for good.
   */
  get missedSeconds(): number {
    if (this.firstAudioAt === null) return 0
    return Math.max(0, nowSeconds() - this.firstAudioAt - this.recordedSeconds)
  }

  /** How long since audio last arrived. Large while the page is stalled. */
  get secondsSinceAudio(): number {
    return this.lastAudioAt === null ? 0 : nowSeconds() - this.lastAudioAt
  }

  /** Ask for the microphone and load the worklet. Nothing is captured yet. */
  async open(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: MIC_CONSTRAINTS,
    })
    try {
      this.context = new AudioContext()
      await this.context.audioWorklet.addModule(
        `${import.meta.env.BASE_URL}pcm-capture-worklet.js`,
      )
      this.node = new AudioWorkletNode(this.context, 'pcm-capture', {
        processorOptions: { targetRate: TARGET_SAMPLE_RATE },
      })
      this.node.port.onmessage = (event) => this.onMessage(event.data)
      this.source = this.context.createMediaStreamSource(this.stream)
    } catch (error) {
      this.release()
      throw error
    }
    this.watch(this.stream)
  }

  private watch(stream: MediaStream): void {
    for (const track of stream.getAudioTracks()) {
      track.addEventListener('ended', () => void this.reacquire())
    }
  }

  /** Swap in a new microphone stream without touching the worklet or chunks. */
  private async reacquire(): Promise<void> {
    if (this.stopped || !this.context || !this.node) return
    this.source?.disconnect()
    this.stream?.getTracks().forEach((track) => track.stop())
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS })
      if (this.stopped || !this.context || !this.node) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      this.stream = stream
      this.source = this.context.createMediaStreamSource(stream)
      this.source.connect(this.node)
      this.watch(stream)
      this.onInputRestored?.()
    } catch {
      this.stream = null
      this.source = null
      this.onInputLost?.()
    }
  }

  /**
   * Get audio flowing again: a new microphone if the old one is gone, a
   * resumed AudioContext if the browser suspended it, the screen lock again
   * (a page loses it whenever it is hidden), and another go at any part that
   * failed to upload. Runs by itself when the page becomes visible; the
   * dialog's button calls it too, because some browsers only resume audio from
   * a tap.
   */
  async resume(): Promise<void> {
    if (this.stopped || !this.context) return
    const live = this.stream?.getAudioTracks().some((track) => track.readyState === 'live')
    if (!live) await this.reacquire()
    if (this.context && this.context.state !== 'running') {
      await this.context.resume().catch(() => undefined)
    }
    await this.holdScreen()
    this.retryFailed()
  }

  /** Keep the screen from timing out while recording. Best effort. */
  private async holdScreen(): Promise<void> {
    if (this.stopped || this.wakeLock || !('wakeLock' in navigator)) return
    try {
      const lock = await navigator.wakeLock.request('screen')
      lock.addEventListener('release', () => {
        if (this.wakeLock === lock) this.wakeLock = null
      })
      this.wakeLock = lock
    } catch {
      // Refused (no user activation, battery saver, unsupported). Recording
      // still works; the screen may just time out.
    }
  }

  /** Re-queue the parts whose uploads exhausted their retries. */
  private retryFailed(): void {
    if (this.retrying || this.failedChunks.size === 0 || this.permanentError) return
    this.retrying = true
    const pending = [...this.failedChunks]
    this.uploadQueue = this.uploadQueue
      .then(async () => {
        for (const [index, buffer] of pending) {
          if (this.failedChunks.has(index)) await this.upload(index, buffer)
        }
      })
      .finally(() => {
        this.retrying = false
      })
  }

  /** Start capturing into the given meeting. */
  async begin(meetingId: string): Promise<void> {
    if (!this.context || !this.node || !this.source) throw new Error('open() first')
    this.meetingId = meetingId
    this.source.connect(this.node)
    // The worklet writes no output, so this plays silence. It is connected
    // because a node that reaches no destination may never be scheduled.
    this.node.connect(this.context.destination)
    await this.context.resume()
    this.lastAudioAt = nowSeconds()
    document.addEventListener('visibilitychange', this.onVisibility)
    window.addEventListener('online', this.onOnline)
    await this.holdScreen()
  }

  /** Stop capturing. Uploads already queued keep going; see `finish`. */
  async stop(): Promise<void> {
    if (this.stopped) return
    this.stopped = true
    if (this.node) {
      // Drain the worklet's partly-filled frame so the last second is kept.
      await Promise.race([
        new Promise<void>((resolve) => {
          this.onFlushed = resolve
          this.node?.port.postMessage('flush')
        }),
        sleep(FLUSH_TIMEOUT_MS),
      ])
    }
    this.release()
    if (this.pendingSamples > 0) this.seal()
  }

  /**
   * Wait for every upload, retry the ones that failed, then tell the server
   * the recording is complete -- which starts transcription. Safe to call
   * again after it throws: chunks already stored are not re-sent.
   */
  finish(): Promise<string> {
    // SINGLE-FLIGHT. Stop-and-save and an unmount can both get here; two
    // completions would race to write the transcription trigger. The server
    // guards it too, but there is no reason to send the second one.
    this.finishing ??= this.runFinish().finally(() => {
      this.finishing = null
    })
    return this.finishing
  }

  private async runFinish(): Promise<string> {
    if (!this.meetingId) throw new Error('begin() first')
    await this.uploadQueue
    for (const [index, buffer] of [...this.failedChunks]) {
      await this.upload(index, buffer)
    }
    if (this.permanentError) throw this.permanentError
    if (this.failedChunks.size > 0) {
      throw new Error(
        `${this.failedChunks.size} part(s) of the recording could not be uploaded. Check your connection and try again.`,
      )
    }
    if (this.chunks.length === 0) throw new Error('Nothing was recorded.')
    const last = this.chunks[this.chunks.length - 1]
    await meetingsApi.deviceRecording.complete(
      this.meetingId,
      this.chunks,
      last.startedAt + last.sizeBytes / 2 / TARGET_SAMPLE_RATE,
    )
    return this.meetingId
  }

  /**
   * Release the microphone and throw the recording away, server side too, so
   * it does not stay listed as live. Best effort: the person has already
   * decided, and a failure here only leaves a row to tidy up.
   */
  abandon(): void {
    this.stopped = true
    this.release()
    if (this.meetingId) {
      void meetingsApi.deviceRecording.discard(this.meetingId).catch(() => undefined)
    }
  }

  private release(): void {
    document.removeEventListener('visibilitychange', this.onVisibility)
    window.removeEventListener('online', this.onOnline)
    void this.wakeLock?.release().catch(() => undefined)
    this.wakeLock = null
    this.stream?.getTracks().forEach((track) => track.stop())
    this.source?.disconnect()
    this.node?.disconnect()
    if (this.node) this.node.port.onmessage = null
    void this.context?.close().catch(() => undefined)
    this.stream = null
    this.source = null
    this.node = null
    this.context = null
  }

  private onMessage(data: unknown): void {
    if (data === 'flushed') {
      this.onFlushed?.()
      this.onFlushed = null
      return
    }
    if (!(data instanceof ArrayBuffer) || !this.meetingId) return
    const frame = new Int16Array(data)
    // Wall clock at the frame's FIRST sample, measured rather than derived
    // from the sample count, so the manifest's drift check means something.
    const now = nowSeconds()
    const frameStartedAt = now - frame.length / TARGET_SAMPLE_RATE
    this.firstAudioAt ??= frameStartedAt
    this.lastAudioAt = now
    this.recordedSamples += frame.length
    let offset = 0
    while (offset < frame.length) {
      if (this.chunkStartedAt === null) {
        this.chunkStartedAt = frameStartedAt + offset / TARGET_SAMPLE_RATE
      }
      const take = Math.min(CHUNK_SAMPLES - this.pendingSamples, frame.length - offset)
      this.pending.push(frame.subarray(offset, offset + take))
      this.pendingSamples += take
      offset += take
      if (this.pendingSamples === CHUNK_SAMPLES) this.seal()
    }
    this.onLevel?.(rmsOf(frame))
  }

  private seal(): void {
    const pcm = new Int16Array(this.pendingSamples)
    let at = 0
    for (const part of this.pending) {
      pcm.set(part, at)
      at += part.length
    }
    const index = this.chunks.length
    this.chunks.push({
      index,
      sizeBytes: pcm.byteLength,
      startedAt: this.chunkStartedAt ?? nowSeconds(),
      rms: rmsOf(pcm),
    })
    this.pending = []
    this.pendingSamples = 0
    this.chunkStartedAt = null
    const buffer = pcm.buffer
    // Sequential: one upload at a time keeps the API to one request per
    // recording, and an ordered queue makes "what is left" easy to reason about.
    this.uploadQueue = this.uploadQueue.then(() => this.upload(index, buffer))
    this.report()
  }

  private async upload(index: number, buffer: ArrayBuffer): Promise<void> {
    if (!this.meetingId || this.permanentError) return
    for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt++) {
      try {
        await meetingsApi.deviceRecording.uploadChunk(this.meetingId, index, buffer)
        this.failedChunks.delete(index)
        this.uploaded.add(index)
        this.report()
        return
      } catch (error) {
        if (isPermanent(error)) {
          // Stop rather than carry on capturing into a recording that will
          // store nothing more -- and say so, instead of showing "Recording".
          this.permanentError = error
          this.report()
          void this.stop()
          this.onRefused?.(error)
          return
        }
        if (attempt < UPLOAD_ATTEMPTS) {
          await sleep(Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS))
        }
      }
    }
    this.failedChunks.set(index, buffer)
    this.report()
  }

  private report(): void {
    this.onProgress?.({
      captured: this.chunks.length,
      uploaded: this.uploaded.size,
      failed: this.failedChunks.size,
    })
  }
}
